import { Compass, Eye, MapPin, Clock, Check } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { formatHours } from "../lib/format";
import type { CorridorOption, UiLang } from "../lib/types";

interface Props {
  corridors: CorridorOption[];
  selected: string;
  lang: UiLang;
  /**
   * Velger ruta. Kalleren fører blikket videre ned til valgene.
   *
   * Dette var før det samme som å åpne presentasjonen, og det var feil: et
   * klikk på et kort i en radiogruppe betyr «jeg velger denne», ikke «vis meg
   * mer». Man valgte en rute, fikk en dialog, lukket den — og satt igjen med
   * en side som så nøyaktig ut som før. Se `onPreview`.
   */
  onSelect: (corridorId: string) => void;
  /** Åpner presentasjonen av ruta. Egen knapp, fordi det er en egen handling. */
  onPreview: (corridorId: string) => void;
}

/**
 * Velger hvilken strekning som planlegges.
 *
 * Ligger over resten av skjemaet fordi valget bestemmer alt annet: hvilke
 * reisemåter som finnes (det går ikke tog nord for Bodø) og hvilke stopp som
 * kan velges.
 *
 * Kortene er bygget rundt bildet. Den forrige utgaven var sju identiske hvite
 * bokser med grå tekst — i en app som handler om hvordan Norge ser ut, og som
 * har et fotografi av hvert eneste sted liggende ubrukt. Nå ser man Nærøyfjorden
 * før man leser om den.
 */

/**
 * Reiser som er satt sammen av flere strekninger.
 *
 * Skilt på id framfor på lengde. Terskelen var «over femten timer», og da
 * havnet Bergen–Trondheim på 13,5 timer blant grunnstrekningene selv om den er
 * satt sammen av to av dem. Sammensetningen er et faktum om ruta, ikke noe
 * som skal utledes av hvor lang den tilfeldigvis er.
 */
const JOURNEY_IDS = new Set(["oslo-tromso", "oslo-bodo", "bergen-trondheim"]);

export function CorridorPicker({ corridors, selected, lang, onSelect, onPreview }: Props) {
  const t = dictionaries[lang];
  if (corridors.length === 0) return null;

  const routes = corridors.filter((c) => !JOURNEY_IDS.has(c.id));
  const journeys = corridors.filter((c) => JOURNEY_IDS.has(c.id));

  const grid = (list: CorridorOption[], label: string, offset: number) => (
    <div className="corridor-grid" role="radiogroup" aria-label={label}>
      {list.map((corridor, index) => {
        const active = corridor.id === selected;
        return (
          /*
           * Kortet og kikk-knappen er søsken, ikke nøstet. En knapp inni en
           * knapp er ugyldig HTML, og nettleserne løser det hver på sin måte —
           * som regel ved at den indre aldri får klikket sitt.
           */
          <div className="corridor-cell" key={corridor.id}>
            <button
              role="radio"
              aria-checked={active}
              className={`corridor ${active ? "corridor--on" : ""}`}
              /* Kortene tones inn etter hverandre, så lista bygger seg opp
                 framfor å bare stå der. Forsinkelsen er liten nok til at det
                 oppleves som én bevegelse. */
              style={{ animationDelay: `${(offset + index) * 55}ms` }}
              onClick={() => onSelect(corridor.id)}
            >
              <span className="corridor-photo">
                {corridor.image && (
                  <img src={corridor.image.src} alt="" loading="lazy" aria-hidden="true" />
                )}
                <span className="corridor-scrim" />

                <span className="corridor-route">
                  {corridor.startName} <span className="corridor-arrow">→</span> {corridor.endName}
                </span>
                <span className="corridor-region">{corridor.region}</span>

                {active && (
                  <span className="corridor-check" aria-hidden="true">
                    <Check size={13} strokeWidth={3.5} />
                  </span>
                )}
              </span>

              <span className="corridor-body">
                <span className="corridor-tagline">{corridor.tagline}</span>
                <span className="corridor-meta">
                  <span>
                    <MapPin size={11} /> {t.stopCount(corridor.stopCount)}
                  </span>
                  <span>
                    {/* Avrundet: strekningstiden er et anslag, og minutter brekker linja. */}
                    <Clock size={11} />{" "}
                    {t.endToEnd(formatHours(Math.round(corridor.spanHours), lang))}
                  </span>
                </span>
              </span>
            </button>

            {/*
              Å se ruta før man velger den. Egen knapp fordi det er en egen
              handling — se `onPreview` i Props for hvorfor de to ble skilt.
            */}
            <button
              type="button"
              className="corridor-peek"
              onClick={() => onPreview(corridor.id)}
              aria-label={`${t.previewRoute}: ${corridor.startName}–${corridor.endName}`}
            >
              <Eye size={13} />
              <span>{t.previewRoute}</span>
            </button>
          </div>
        );
      })}
    </div>
  );

  return (
    /* `id` er ankeret «Bytt rute» i skjemaet ruller tilbake til. */
    <div className="card corridor-card" id="route-picker">
      <div className="card-title">
        <Compass size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
        {t.corridorLabel}
      </div>
      <p className="card-sub">{t.corridorHint}</p>

      {grid(routes, t.corridorLabel, 0)}

      {journeys.length > 0 && (
        <>
          <div className="corridor-divider">
            <span>{t.longJourneys}</span>
          </div>
          <p className="card-sub">{t.longJourneysHint}</p>
          {grid(journeys, t.longJourneys, routes.length)}
        </>
      )}
    </div>
  );
}
